import { Celebration, Icon, OinkIcon } from "./Common.jsx";
import { LetterKeyboard, OinkRewardRow, ProgressDots, TestOverlay, TokenRewardRow, playDing, playError } from "./Tests.jsx";
import { HW_KNOWN_DAYS, goItem, historyRow, isListComplete, knownCount, markItem, pickGo } from "../lib/homework.js";
import { audioKeyFor, isSpeechAvailable, prefetchClip, primeVoices, resetRetryBudget, speak as speakText, stopSpeaking, unlockAudio } from "../lib/speech.js";
import { t as ct } from "../lib/content.js";
import { accentsFor } from "../lib/accents.js";

const { useEffect, useRef, useState } = React;

// Homework practice, kid side (LB-364, boards K1–K3 signed off 25 Sep).
//
// One list, one go at a time. A go asks every word on the list (a "both" language list asks
// every row both ways; see pickGo), and pays 1 Oink for every right answer AS IT IS
// ANSWERED (like spelling's practice round), so a child who leaves mid-go keeps what they
// earned. When the last word is answered the go is reported ONCE through onGoFinished, which writes the progress and the history row;
// a go left part way (Quit, or the round going away) reports the words answered so far, marked
// stopped, so every answer counts toward the words (Alan, 29 Sep). If the
// list became complete on this go and the parent set a finishing reward, the tokens go
// through the same onLearnEarn every test uses, so the weekly cap applies exactly as it does
// to a test. Nothing here reaches Learning Coach.
//
// Spellings run in the hear-it-type-it shape: the word is spoken and typed on the docked
// keyboard. A spelling list's words are queued for the shared word bank when the list is saved
// (queueSpellingAudio), and the server voices them straight away, so its generated clip plays, keyed
// exactly as the word bank keys it (the bare word, "teacher" voice); until then, and for any
// word with no clip, the device voice reads it, as it always has. Vocabulary is
// typed too, with a space key, because a sheet gives no distractors; English vocabulary is
// a choice between the list's own meanings.
export function HomeworkRound({ list, today, kidTheme, oinksOn, onEarnOinks, onGoFinished, onLearnEarn, partyEmoji, onClose }) {
  const [phase, setPhase] = useState("intro");
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);
  const [slots, setSlots] = useState([]);
  const [settled, setSettled] = useState(null);
  const [capNotice, setCapNotice] = useState(null);
  const [speechOk, setSpeechOk] = useState(true);
  const [voiceReady, setVoiceReady] = useState(false);
  const [audioKey, setAudioKey] = useState(null);
  const [audioReady, setAudioReady] = useState(false);
  const answersRef = useRef([]);
  const reportedRef = useRef(false);
  const advanceRef = useRef(null);
  const autoplayedRef = useRef(null);
  const item = items[idx];
  const total = list && list.pairs ? list.pairs.length : 0;
  const known = knownCount(list);
  const toLearn = total - known;
  const payOinks = oinksOn && list && list.oinksOn;

  useEffect(() => {
    if (!isSpeechAvailable()) { setSpeechOk(false); return undefined; }
    return primeVoices(() => setVoiceReady(true));
  }, []);
  // The latest callbacks, for the unmount below, which only ever sees the first render's.
  const onGoRef = useRef(onGoFinished); onGoRef.current = onGoFinished;
  const onLearnEarnRef = useRef(onLearnEarn); onLearnEarnRef.current = onLearnEarn;
  const reportStoppedRef = useRef(null);
  // A round that goes away mid-go without Quit still reports what was answered.
  useEffect(() => () => { if (advanceRef.current) clearTimeout(advanceRef.current); stopSpeaking(); if (reportStoppedRef.current) reportStoppedRef.current(); }, []);

  // The clip key for this word, and whether a clip EXISTS, settled before anything is spoken
  // (SpellingTest's pattern: speaking before the key resolved read a word twice on Android, and
  // a 404 found mid-speak left iOS silent; prefetchClip is what closes both).
  // The word, then its "as in" when it has one (Alan, 5 Oct). Three recordings can exist, tried
  // in this order: one of the two together, joined exactly as Learn & Earn's spelling test joins
  // a word and its description (a line that came from the published lists was voiced that way);
  // else the word's own clip and the line's own clip, which saving the list asks the word bank
  // for (queueSpellingAudio), played with a pause between. Any that is missing is read by the
  // device voice in its place.
  const spellText = phase === "going" && item && item.mode === "spell" ? item.answer : "";
  const spellHint = spellText && item.hint ? item.hint : "";
  const [hintKey, setHintKey] = useState(null); // the line's own clip, when the pair is not one clip
  useEffect(() => {
    let live = true;
    setAudioKey(null); setHintKey(null);
    setAudioReady(false);
    if (!spellText) { setAudioReady(true); return () => { live = false; }; }
    (async () => {
      if (spellHint) {
        const both = await audioKeyFor(`${spellText}. ${spellHint}`, "teacher");
        if (both && await prefetchClip(both)) { if (live) { setAudioKey(both); setHintKey("both"); setAudioReady(true); } return; }
      }
      const [k, hk] = await Promise.all([audioKeyFor(spellText, "teacher"), spellHint ? audioKeyFor(spellHint, "teacher") : null]);
      if (!live) return;
      setAudioKey(k); setHintKey(hk);
      await Promise.all([k ? prefetchClip(k) : null, hk ? prefetchClip(hk) : null]);
      if (live) setAudioReady(true);
    })();
    return () => { live = false; };
  }, [spellText, spellHint, idx]);

  // A spelling word is read out a second after it appears, once per word. Same guard shape
  // as SpellingTest's: the word is in the key, so a re-run of this effect cannot read it twice.
  useEffect(() => {
    resetRetryBudget();
    if (phase !== "going" || !item || item.mode !== "spell" || !speechOk || !audioReady) return undefined;
    const key = `${idx}:${item.pairId}`;
    if (autoplayedRef.current === key) return undefined;
    const t = setTimeout(() => { autoplayedRef.current = key; say(true); }, 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, phase, speechOk, voiceReady, audioReady]);

  function say(isAutoplay) {
    if (!item || item.mode !== "spell") return;
    if (!speechOk) { setSpeechOk(true); resetRetryBudget(); }
    // The word, a pause, then its "as in" (a clip, when there is one, already carries both).
    const together = hintKey === "both"; // one clip already says both
    speakText(item.answer, { audioKey, rate: 0.85, isAutoplay, after: item.hint || "", afterKey: together ? null : hintKey, afterDeviceOnly: together, onUnavailable: () => setSpeechOk(false) });
  }

  function start() {
    unlockAudio();
    const drawn = pickGo(list, today);
    setItems(drawn);
    setSlots(drawn.map(() => "pending"));
    setIdx(0); setInput(""); setResult(null); setSettled(null); setCapNotice(null);
    answersRef.current = []; reportedRef.current = false; autoplayedRef.current = null;
    setPhase("going");
  }
  // Reports the answers given so far, once per go. A go the child leaves part way through is
  // reported too, marked stopped (Alan, 29 Sep: "The child shouldn't have to do a full,
  // complete pass in order to register the progress against the words"). Returns what
  // onGoFinished returned, or null when there was nothing to report.
  function report(stopped) {
    if (reportedRef.current) return null;
    const rows = answersRef.current;
    if (stopped && !rows.length) return null;
    reportedRef.current = true;
    const go = onGoRef.current;
    return go ? go(list.code, rows.map((r) => ({ pairId: r.pairId, correct: r.correct })), rows.map((r) => r.row), { stopped }) : null;
  }
  // The finishing tokens, when THIS go (whole or part) made every word known. Returns the
  // tokens credited, or 0 with the cap message when a parent has to decide.
  function payFinish(outcome) {
    const earn = onLearnEarnRef.current;
    if (!(outcome && outcome.completedNow && list.finishTokens > 0 && earn)) return { tokens: 0, capMessage: null };
    const res = earn(list.finishTokens, ct("homework.kid.reward_reason", "Homework practice: {name}, every word known", { name: list.name }), "homework", "finish");
    return res && res.credited ? { tokens: list.finishTokens, capMessage: null } : { tokens: 0, capMessage: (res && res.capMessage) || null };
  }
  reportStoppedRef.current = phase === "going" ? () => payFinish(report(true)) : null;
  function quit() {
    stopSpeaking();
    if (phase === "going") payFinish(report(true));
    if (onClose) onClose();
    setPhase("intro");
  }

  function answer(given, skipped = false) {
    if (result !== null || !item) return;
    const correct = !skipped && markItem(item, given);
    setSlots((prev) => { const next = [...prev]; next[idx] = correct ? "correct" : "wrong"; return next; });
    setResult(correct ? "correct" : "wrong");
    answersRef.current = [...answersRef.current, { pairId: item.pairId, correct, row: historyRow(item, skipped ? "" : given, correct) }];
    if (correct) {
      playDing();
      if (payOinks && onEarnOinks) onEarnOinks(1);
      advanceRef.current = setTimeout(() => next(), 700);
    } else {
      playError();
    }
  }
  function next() {
    setInput(""); setResult(null);
    if (idx + 1 < items.length) { setIdx((i) => i + 1); return; }
    finish();
  }
  function finish() {
    if (reportedRef.current) return;
    stopSpeaking();
    const rows = answersRef.current;
    const outcome = report(false);
    const { tokens, capMessage } = payFinish(outcome);
    if (capMessage) setCapNotice(capMessage);
    setSettled({ ...(outcome || { completedNow: false, known: knownCount(list), total }), tokens, right: rows.filter((r) => r.correct).length, asked: rows.length, misses: rows.filter((r) => !r.correct) });
    setPhase("done");
  }

  if (!list || total === 0) return null;

  if (phase === "intro") {
    const complete = isListComplete(list);
    return (
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid="hw-intro">
        <div className="text-lg font-extrabold text-slate-900 mb-1">{list.name}</div>
        <p className="text-sm text-slate-500 mb-3">{complete
          ? ct("homework.kid.intro_complete", "You know every word here. Keep practising to keep them.")
          : ct("homework.kid.intro_body", "{n} word{plural} still to learn. Each go asks every word.", { n: toLearn, plural: toLearn === 1 ? "" : "s" })}</p>
        <div className="rounded-lg bg-slate-50 px-3 py-2.5 text-sm flex flex-col gap-1.5 mb-3">
          {payOinks && <div className="flex justify-between"><span className="text-slate-500">{ct("homework.kid.intro_oinks_label", "Each right answer")}</span><span className="font-bold flex items-center gap-1"><OinkIcon className="w-4 h-4" />{ct("homework.kid.intro_oinks_value", "1 Oink")}</span></div>}
          <div className="flex justify-between"><span className="text-slate-500">{ct("homework.kid.intro_known_label", "Known so far")}</span><span className="font-bold" data-testid="hw-intro-known">{ct("homework.kid.known_of", "{k} of {n}", { k: known, n: total })}</span></div>
          {list.finishTokens > 0 && !list.finishedAt && <div className="flex justify-between"><span className="text-slate-500">{ct("homework.kid.intro_finish_label", "Once every word is known")}</span><span className="font-bold">{ct("homework.kid.intro_finish_value", "{t} tokens", { t: list.finishTokens })}</span></div>}
        </div>
        <p className="text-xs text-slate-400 mb-3">{ct("homework.kid.intro_note", "Get a word right on two different days and it will be 'known'. Practise as often as you like until {until}.", { until: shortDay(list.until) })}</p>
        <button className={`${kidTheme.primaryBtn} text-white font-bold rounded-xl px-5 py-2.5 text-sm w-full`} onClick={start} data-testid="hw-start">{ct("homework.kid.start", "Start your practice")}</button>
      </div>
    );
  }

  return (
    <TestOverlay onClose={quit} focused={phase === "going"}>
      {phase === "done" && settled ? (
        <div className="relative overflow-hidden bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center" data-testid="hw-done">
          {settled.tokens > 0 && <Celebration variant="pig" emojiOverride={partyEmoji} />}
          <div className="text-xs font-bold text-slate-400 mb-1">{ct("homework.kid.done_kicker", "This go")}</div>
          <div className="font-extrabold text-3xl text-slate-800 mb-1" data-testid="hw-done-score">{settled.right}/{settled.asked}</div>
          <div className="text-sm font-bold text-emerald-600 mb-4" data-testid="hw-done-known">{settled.completedNow
            ? ct("homework.kid.done_all_known", "All {n} known. You've learnt every word.", { n: settled.total })
            : ct("homework.kid.done_known", "{k} of {n} known", { k: settled.known, n: settled.total })}</div>
          {capNotice ? (
            <div className="text-amber-600 font-bold text-base mb-4" data-testid="hw-cap-notice">{capNotice}</div>
          ) : settled.tokens > 0 ? (
            <TokenRewardRow amount={settled.tokens} detail={ct("homework.kid.token_detail", "For learning every word on {name}", { name: list.name })} />
          ) : null}
          {payOinks && settled.right > 0 && <OinkRewardRow op={settled.right} detail={ct("homework.kid.oink_detail", "For practising")} />}
          {settled.misses.length > 0 && (
            <div className="text-left mb-4" data-testid="hw-done-misses">
              <div className="text-xs font-bold text-slate-500 mb-1.5">{ct("homework.kid.go_back_for", "To go back for")}</div>
              <div className="flex flex-col gap-1.5">
                {settled.misses.map((m, i) => (
                  <div key={i} className="rounded-lg px-3 py-2 text-xs font-semibold bg-rose-50 text-rose-700 flex items-center justify-between gap-2">
                    {/* A spelling miss is one word, not a prompt and an answer. */}
                    <span className="truncate">{m.row.q === m.row.answer ? <b>{m.row.answer}</b> : m.row.q}</span>
                    <span className="text-rose-500 truncate">{m.row.given ? <><s className="opacity-70">{m.row.given}</s> </> : null}{m.row.q === m.row.answer ? null : <b>{m.row.answer}</b>}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <button className={`${kidTheme.primaryBtn} text-white font-bold rounded-xl px-5 py-2.5 text-sm w-full mb-2`} onClick={start} data-testid="hw-again">{ct("homework.kid.again", "Go again")}</button>
          <button className="w-full text-sm font-bold text-slate-600 py-2" onClick={quit}>{ct("tests.back_to_le", "Back to Learn & Earn")}</button>
        </div>
      ) : item ? (
        <div key={`hw-${idx}`} className="bg-white rounded-2xl px-3 py-5 shadow-sm border border-slate-100 text-center" data-testid="hw-question">
          <div className="flex flex-col mb-3">
            <ProgressDots slots={slots} />
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-slate-400 font-medium text-left">{ct("homework.kid.position", "Word {i} of {n} this go", { i: idx + 1, n: items.length })}</div>
              <button className="lb-inline-quit text-xs text-slate-400 font-semibold flex-shrink-0 ml-2" onClick={quit}>{ct("tests.quit", "Quit")}</button>
            </div>
          </div>
          {item.mode === "spell" ? (
            <>
              {/* Shown as well as spoken, above Listen, as in Learn & Earn's spelling test (LB-292):
                  a child who misheard it, or cannot hear it, still knows which word is meant. */}
              {item.hint && result === null && <p className="text-center text-sm text-slate-500 mb-3 px-4" data-testid="hw-hint">{item.hint}</p>}
              {speechOk ? (
                <button onClick={() => say(false)} className="mx-auto flex items-center gap-2 bg-amber-50 text-amber-700 font-bold rounded-lg px-5 py-3 mb-4">{ct("tests.listen", "Listen")}</button>
              ) : (
                <button onClick={() => say(false)} className="w-full text-left text-amber-600 bg-amber-50 rounded-xl px-4 py-3 mb-4 text-base font-semibold">{ct("spelling.audio_unavailable", "Audio isn't available right now — tap to try again, or ask a parent to read the word aloud.")}</button>
              )}
              <LetterKeyboard value={input} disabled={result !== null} onEnter={() => answer(input)} maxLength={24} onChange={setInput} />
            </>
          ) : item.mode === "type" ? (
            <>
              <div className="text-xs font-bold text-slate-500 mb-1">{item.dir === "lang2en"
                ? ct("homework.kid.ask_english", "What is the English for")
                : ct("homework.kid.ask_language", "What is the {lang} for", { lang: list.lang || ct("homework.kid.other_language", "other language") })}</div>
              <div className="text-2xl font-extrabold text-slate-900 mb-4" data-testid="hw-prompt">{item.prompt}</div>
              <LetterKeyboard value={input} disabled={result !== null} allowSpace placeholder={ct("homework.kid.type_placeholder", "Type your answer")} onEnter={() => answer(input)} maxLength={40} onChange={setInput}
                accents={list.type === "language" && item.dir !== "lang2en" ? accentsFor(list.lang) : null} />
            </>
          ) : (
            <>
              <div className="text-xs font-bold text-slate-500 mb-1">{ct("homework.kid.ask_meaning", "What does this mean")}</div>
              <div className="text-2xl font-extrabold text-slate-900 mb-4" data-testid="hw-prompt">{item.prompt}</div>
              <div className="flex flex-col gap-2 mb-2">
                {(item.options || []).map((opt) => (
                  <button key={opt} disabled={result !== null} onClick={() => answer(opt)} className={`w-full text-left rounded-xl px-4 py-3 text-sm font-semibold border ${result !== null && opt === item.answer ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-slate-200 text-slate-800"}`} data-testid="hw-option">{opt}</button>
                ))}
              </div>
            </>
          )}
          {item.mode !== "choose" && result === null && (
            <>
              <button disabled={!input.trim()} className={`${kidTheme.primaryBtn} text-white font-bold rounded-xl px-5 py-2.5 text-base w-full mt-6 disabled:opacity-40`} onClick={() => answer(input)} data-testid="hw-check">{ct("tests.check_button", "Check")}</button>
              <button className="w-full mt-2 text-sm font-bold text-slate-500 py-2" data-testid="hw-skip" onClick={() => answer("", true)}>{ct("tests.skip", "Skip this word")}</button>
            </>
          )}
          {result !== null && (
            <div className={`font-bold text-base mt-3 flex items-center justify-center gap-1.5 ${result === "correct" ? "text-emerald-500" : "text-rose-500"}`} data-testid="hw-result">
              <Icon id={result === "correct" ? "ic-celebrate" : "ic-close"} size={18} hue={result === "correct" ? "pink" : "rose"} />
              {result === "correct" ? ct("homework.kid.correct", "Correct") : ct("homework.kid.wrong", "Not quite, it was {answer}", { answer: item.answer })}
            </div>
          )}
          {result === "wrong" && (
            <button className={`${kidTheme.primaryBtn} text-white font-bold rounded-xl px-4 py-2.5 text-sm w-full mt-3`} onClick={next} data-testid="hw-continue">{ct("spelling.continue_button", "Continue")}</button>
          )}
          <p className="text-[11px] text-slate-400 mt-4">{ct("homework.kid.known_rule", "Right on {d} different days and a word is known", { d: HW_KNOWN_DAYS })}</p>
        </div>
      ) : null}
    </TestOverlay>
  );
}

// "Fri 2 Oct" from YYYY-MM-DD, without a time zone shift.
export function shortDay(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd || "";
  const d = new Date(`${ymd}T12:00:00`);
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }).replace(",", "");
}

// The tile group on the child's Learn & Earn page: one tile per live list, with a progress
// bar, and the round for whichever is open.
// `heading` is off when HomeworkSubmit already draws the section header row above these tiles.
export function HomeworkTiles({ lists, activeCode, onPick, kidTheme, heading = true }) {
  // Alan, 26 Sep: picking a list anchors the page on THIS heading, not on "Pick a game".
  const headRef = useRef(null);
  if (!lists || !lists.length) return null;
  const pick = (code) => { onPick(code); setTimeout(() => headRef.current && headRef.current.scrollIntoView({ behavior: "smooth", block: "start" }), 30); };
  return (
    <div className="mb-3" data-testid="hw-tiles" ref={headRef} style={{ scrollMarginTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}>
      {heading && <div className="text-sm font-extrabold text-slate-900 mb-2">{ct("homework.kid.group_title", "Homework")}</div>}
      <div className="flex flex-col gap-2">
        {lists.map((l) => {
          const k = knownCount(l); const n = l.pairs.length; const active = activeCode === l.code;
          const from = l.adultName ? ct("homework.kid.from", "from {name}", { name: l.adultName }) : "";
          return (
            <button key={l.code} data-testid={`hw-tile-${l.code}`} onClick={() => pick(l.code)} className={`rounded-lg py-2.5 px-3 text-left ${active ? kidTheme.activeTab : "kc kc-play text-slate-900"}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className={`text-sm font-bold truncate ${active ? "" : "kc-title"}`}>{l.name}</div>
                  <div className={`text-[11px] font-semibold ${active ? "text-white/90" : "text-slate-500"}`}>{[from, ct("homework.kid.known_of", "{k} of {n}", { k, n }) + " " + ct("homework.kid.known_word", "known"), ct("homework.kid.by", "by {day}", { day: shortDay(l.until) })].filter(Boolean).join(" · ")}</div>
                </div>
                {k === n
                  ? <span className={`text-[11px] font-extrabold rounded-full px-2 py-0.5 flex-shrink-0 ${active ? "bg-white text-teal-800" : "bg-emerald-100 text-emerald-800"}`}>{ct("homework.kid.tag_known", "Known")}</span>
                  : (l.goes || 0) === 0
                    ? <span className={`text-[11px] font-extrabold rounded-full px-2 py-0.5 flex-shrink-0 ${active ? "bg-white text-slate-900" : ""}`} style={active ? {} : { background: "#FFC629", color: "#1B2A4A" }}>{ct("homework.kid.tag_new", "New")}</span>
                    : null}
              </div>
              <div className={`h-1.5 rounded-full mt-2 ${active ? "bg-white/30" : "bg-slate-200"}`} aria-hidden="true"><div className={`h-1.5 rounded-full ${active ? "bg-white" : "bg-indigo-500"}`} style={{ width: `${n ? Math.round((100 * k) / n) : 0}%` }} /></div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Exported for the harness: the item a go shows for a pair.
export { goItem as homeworkGoItem };
